import React, { cloneElement, isValidElement, useId, useMemo, useState } from "react";
import { Divider, ListItemIcon, ListSubheader, Menu, MenuItem, Typography } from "@mui/material";

const DEFAULT_ANCHOR_ORIGIN = { vertical: "bottom", horizontal: "right" };
const DEFAULT_TRANSFORM_ORIGIN = { vertical: "top", horizontal: "right" };

const MENU_PAPER_SX = {
  border: "1px solid",
  borderColor: "rgba(157, 106, 196, 0.4)",
  borderRadius: "14px",
  bgcolor: "background.paper",
  boxShadow: "0 8px 20px rgba(17, 24, 39, 0.08)",
};

const MENU_ITEM_SX = {
  display: "flex",
  alignItems: "center",
  gap: 1.5,
  py: 1.5,
  px: 2,
};

const MENU_ITEM_ICON_SX = {
  minWidth: 0,
  color: "primary.main",
  "& svg": {
    color: "inherit",
  },
};

function renderItemLabel(label, selected) {
  if (typeof label === "string" || typeof label === "number") {
    return (
      <Typography variant="body2" color={selected ? "primary.main" : "text.primary"} fontWeight={selected ? 600 : 400}>
        {label}
      </Typography>
    );
  }

  return label;
}

/**
 * @typedef {object} UniversalDropdownItem
 * @property {string | number} id
 * @property {React.ReactNode} label
 * @property {React.ReactNode} [icon]
 * @property {() => void | Promise<void>} [onClick]
 * @property {boolean} [disabled]
 * @property {boolean} [selected]
 * @property {string} [testId]
 * @property {object} [sx]
 * @property {boolean} [divider] - Renders a non-interactive separator row.
 * @property {string} [subheader] - Renders a non-interactive section header row.
 */

/**
 * @typedef {object} UniversalDropdownProps
 * @property {React.ReactNode | ((args: { onClick: (event: React.MouseEvent) => void, open: boolean }) => React.ReactNode)} trigger
 * @property {UniversalDropdownItem[]} items
 * @property {boolean} [matchTriggerWidth]
 * @property {import("@mui/material").MenuProps} [menuProps]
 */

/**
 * Reusable dropdown menu with platform-standard menu chrome and item layout.
 *
 * @param {UniversalDropdownProps} props
 * @returns {React.ReactNode}
 */
export default function UniversalDropdown({ trigger, items = [], matchTriggerWidth = false, menuProps = {} }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const menuId = useId();
  const triggerWidth = matchTriggerWidth && anchorEl ? anchorEl.offsetWidth : undefined;

  const {
    slotProps: menuSlotProps = {},
    anchorOrigin = DEFAULT_ANCHOR_ORIGIN,
    transformOrigin = DEFAULT_TRANSFORM_ORIGIN,
    onClose: onMenuClose,
    ...restMenuProps
  } = menuProps;

  const mergedMenuSlotProps = useMemo(
    () => ({
      ...menuSlotProps,
      paper: {
        elevation: 0,
        ...(menuSlotProps.paper ?? {}),
        sx: {
          ...MENU_PAPER_SX,
          ...(triggerWidth != null
            ? {
                width: triggerWidth,
                minWidth: triggerWidth,
                maxWidth: triggerWidth,
                boxSizing: "border-box",
              }
            : {}),
          ...(menuSlotProps.paper?.sx ?? {}),
        },
      },
    }),
    [menuSlotProps, triggerWidth],
  );

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (...args) => {
    setAnchorEl(null);
    onMenuClose?.(...args);
  };

  const renderTrigger = () => {
    if (typeof trigger === "function") {
      return trigger({ onClick: handleOpen, open });
    }

    if (isValidElement(trigger)) {
      const existingOnClick = trigger.props.onClick;

      return cloneElement(trigger, {
        onClick: (event) => {
          existingOnClick?.(event);
          handleOpen(event);
        },
        "aria-haspopup": "menu",
        "aria-expanded": open ? "true" : undefined,
        "aria-controls": open ? menuId : undefined,
      });
    }

    return trigger;
  };

  return (
    <>
      {renderTrigger()}
      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        marginThreshold={matchTriggerWidth ? 0 : undefined}
        variant={matchTriggerWidth ? "menu" : "selectedMenu"}
        slotProps={mergedMenuSlotProps}
        {...restMenuProps}
      >
        {items.map((item) => {
          if (item.subheader) {
            return (
              <ListSubheader
                key={item.id}
                component="li"
                disableSticky
                sx={{
                  bgcolor: "transparent",
                  fontSize: "0.75rem",
                  lineHeight: "normal",
                  fontWeight: 600,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: "text.disabled",
                  pt: 1.5,
                  pb: 0.5,
                  px: 1.25,
                  ...(item.sx ?? {}),
                }}
              >
                {item.subheader}
              </ListSubheader>
            );
          }

          if (item.divider) {
            return (
              <Divider
                key={item.id}
                component="li"
                sx={{
                  borderColor: "rgba(157, 106, 196, 0.2)",
                  my: 0.25,
                  mx: 0.75,
                  ...(item.sx ?? {}),
                }}
              />
            );
          }

          return (
            <MenuItem
              key={item.id}
              disabled={item.disabled}
              selected={Boolean(item.selected)}
              aria-selected={item.selected ? "true" : undefined}
              data-testid={item.testId}
              sx={{
                ...MENU_ITEM_SX,
                ...(item.sx ?? {}),
              }}
              onClick={async () => {
                try {
                  await item.onClick?.();
                } finally {
                  handleClose();
                }
              }}
            >
              {item.icon ? <ListItemIcon sx={MENU_ITEM_ICON_SX}>{item.icon}</ListItemIcon> : null}
              {renderItemLabel(item.label, Boolean(item.selected))}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
